//	Islands client runtime (Svelte 5). Replaces the old self-fetch of the
//	SvelteKit bootstrap + the _reperio route: scans the SSR markup for
//	._hydratable markers emitted by Hydrator.svelte and mounts the matching
//	component from the theme's HydratableComponents whitelist.
//
//	Wire format (shared contract with Hydrator.svelte — do not change one
//	without the other): class `_hydratable`, data-component, data-props,
//	data-context, plus `window.reperioContext` injected by hooks.server.ts
//	and `window.reperioContextLocal` injected by the page.
import { mount } from 'svelte';
import { readable, writable } from 'svelte/store';
import * as HydratableComponents from '$domain/HydratableComponents';
import { DOMAIN_VIEWS as domainViewsBlocks } from '$domain/blocks';
import { DOMAIN_VIEWS as domainViewsEditorBlocks } from '$domain/editorBlocks';

declare global {
	interface Window {
		reperioContext?: unknown;
		reperioContextLocal?: unknown;
	}
}

//	The try/catch around mount() only sees synchronous throws; the detached-DOM
//	race class surfaces later in effect flushes. Tag global failures with an
//	islands prefix so they are greppable in RUM/console tooling.
window.addEventListener('error', (e) => {
	console.error('[islands] uncaught error:', e.error ?? e.message);
});
window.addEventListener('unhandledrejection', (e) => {
	console.error('[islands] unhandled rejection:', e.reason);
});

function boot(): void {
	//	writable to match the SSR page context (themes assign to $context.*)
	const context = writable(window.reperioContext);
	const contextLocal = writable(window.reperioContextLocal ?? {});

	for (const x of Array.from(document.querySelectorAll('._hydratable'))) {
		const item = x as HTMLElement;

		//	mounting an outer island detaches the markers nested inside it;
		//	mounting those again would duplicate onMount side effects
		//	(fetches, observers) into off-document targets
		if (!item.isConnected) {
			continue;
		}

		if (!item.dataset.props || !item.dataset.component) {
			continue;
		}

		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const COMPONENT = (HydratableComponents as any)[item.dataset.component];
		if (!COMPONENT) {
			console.error(`[islands] hydratable component not whitelisted: ${item.dataset.component}`);
			continue;
		}

		const ctx = new Map<string, unknown>([
			['context', context],
			['contextLocal', contextLocal],
			//	DomainView resolves per-domain block/editorBlock overrides from
			//	this; without it every island falls back to the generic
			//	core.components markup (unsized images, no theme layout)
			['DOMAIN_VIEWS', { blocks: domainViewsBlocks, editorBlocks: domainViewsEditorBlocks }],
			//	tells Hydrator (and any Hydrator nested in this subtree) that
			//	its marker was already consumed: render content directly,
			//	never emit a marker or swap branches post-mount
			['islandRuntime', true],
		]);

		//	parent block context (column count / background color) serialized by
		//	Hydrator.svelte; entries typed "store/readable" are rebuilt as stores
		if (item.dataset.context) {
			try {
				const parentCtx = JSON.parse(decodeURIComponent(item.dataset.context));
				for (const [key, entry] of Object.entries<{ value: unknown, type?: string }>(parentCtx)) {
					ctx.set(key, entry?.type === 'store/readable' ? readable(entry.value) : entry?.value);
				}
			} catch (_e) {
				// malformed marker — mount without parent context
			}
		}

		//	one malformed marker or throwing component must not abort the
		//	remaining islands (the menu died this way on dilemaveche)
		try {
			const props = JSON.parse(decodeURIComponent(item.dataset.props));

			const target = document.createElement('div');
			target.style.display = 'contents';
			item.replaceWith(target);

			mount(COMPONENT, { target, props, context: ctx });
		} catch (e) {
			console.error(`[islands] mount failed: ${item.dataset.component}`, e);
		}
	}
}

if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', boot);
} else {
	boot();
}
